import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getLabel } from '@/utils/labelUtils';

import { ProductCard } from '@/components/ProductCard';
import { getProductCardImageAlt } from '@/components/ProductCard/helpers';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import { type WatchHit } from '@/integrations/algolia/types';
import type { ProductCatalogModeCtaStyle } from '@/lib/types';
import { theme } from '@/theme';

import { useProductListCardCommon } from './helper';

interface WatchCardProps {
  bottomSlot?: React.ReactNode;
  cardClickHandler?: () => void;
  className?: string;
  customOrderFun?: () => Promise<object[] | undefined>;
  hideFavorite?: boolean;
  hit: WatchHit;
  href?: string;
  imageSlot?: React.ReactNode;
  priority?: boolean;
  productCatalogMode?: boolean;
  productCatalogModeCtaStyle?: ProductCatalogModeCtaStyle;
  sizes?: string;
  style?: React.CSSProperties;
  type?: string;
}

export const WatchCard = ({
  bottomSlot,
  cardClickHandler,
  className,
  customOrderFun,
  hideFavorite = false,
  hit,
  href,
  imageSlot,
  priority = false,
  productCatalogMode: propProductCatalogMode,
  productCatalogModeCtaStyle = 'DISCOVER',
  sizes,
  style,
  type,
}: WatchCardProps) => {
  const { formatMessage } = useIntl();
  const {
    contextProductCatalogMode,
    detailPath,
    handleClickEvent,
    imageUrl,
    isLocalProcessing,
    isProcessing,
    name,
    setIsLocalProcessing,
  } = useProductListCardCommon(hit);

  const productCatalogMode =
    propProductCatalogMode !== undefined
      ? propProductCatalogMode
      : contextProductCatalogMode;

  const useCommerceCta = Boolean(customOrderFun);

  const commerceOrderFun = useCallback(async () => {
    if (!customOrderFun) return;
    setIsLocalProcessing(true);
    try {
      return await customOrderFun();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLocalProcessing(false);
    }
  }, [customOrderFun, setIsLocalProcessing]);

  const effectiveHref = href ?? detailPath;
  const isExternalHref = href?.startsWith('http');

  const handleClick = async (e: React.MouseEvent) => {
    if (useCommerceCta) {
      return handleClickEvent(e, commerceOrderFun, false);
    }
    if (isExternalHref && productCatalogMode) {
      e.preventDefault();
      e.stopPropagation();
      window.location.href = href!;
      return;
    }
    return handleClickEvent(e, undefined, productCatalogMode);
  };

  if (!hit) return null;
  const label = getLabel(hit.marketing_status, hit.launch_date);

  let imageClassName;
  switch (type) {
    case 'regular':
      imageClassName = 'watch-soldier-image';
      break;
    case 'square':
      imageClassName = 'square-card';
      break;
    case 'two-heros':
      imageClassName = 'two-heros';
      style = { ...style };
      break;
    default:
      imageClassName = 'watch-soldier-image';
  }
  return (
    <ProductCard
      backgroundColor={theme.colors.lightGrey}
      cardClickHandler={cardClickHandler}
      className={className}
      cta={
        <>
          {(!productCatalogMode || productCatalogModeCtaStyle !== 'HIDDEN') && (
            <ProductCard.CTA
              cta={
                useCommerceCta || !productCatalogMode
                  ? (hit.call_to_action as OrderAppCtaType)
                  : 'DISCOVER'
              }
              data-test-id='WatchCardProductCardCTA'
              disabled={isProcessing}
              isProcessing={isLocalProcessing}
              onClick={handleClick}
            />
          )}
          {bottomSlot}
        </>
      }
      ctaType='bottom'
      data-test-id='WatchCardProductCard'
      href={effectiveHref}
      sku={hit.objectID}
      style={style}
      topSlot={
        !hideFavorite && (
          <ProductCard.Favorite
            data-test-id='WatchCardProductCardFavorite'
            name={name}
            price={hit.price}
            sku={hit.objectID}
            theCurrency={hit.currency}
            type={hit.product_type}
          />
        )
      }
      type={hit.product_type}
    >
      <ProductCard.Zone>
        {imageSlot ?? (
          <ProductCard.Image
            alt={getProductCardImageAlt({
              formatMessage,
              name,
              sku: hit.objectID,
            })}
            className={`${imageClassName} watch-card`}
            data-test-id='WatchCardProductCardImage'
            loading={priority ? 'eager' : 'lazy'}
            priority={priority}
            productType='watch'
            sizes={sizes ?? '(max-width: 600px) 50vw, 18vw'}
            src={imageUrl ?? ''}
            withBackgroundPlaceholderBackground={false}
          />
        )}
      </ProductCard.Zone>
      <ProductCard.Zone>
        <ProductCard.CardZoneLabelPriceRefGroup>
          <ProductCard.Sku data-test-id='WatchCardProductCardSku'>
            {hit.objectID}
          </ProductCard.Sku>
          <ProductCard.Name data-test-id='WatchCardProductCardName'>
            {name}
          </ProductCard.Name>
          {!!hit.price && (
            <ProductCard.Price
              data-test-id='WatchCardProductCardPrice'
              price={{ amount: hit.price, currency: hit.currency }}
            />
          )}
          {label && (
            <ProductCard.Label
              data-test-id='WatchCardProductCardLabel'
              text={<FormattedMessage {...label} />}
            />
          )}
        </ProductCard.CardZoneLabelPriceRefGroup>
      </ProductCard.Zone>
    </ProductCard>
  );
};
